import { ImageInput } from "@circle-react-uikit/ImageInput";
import {
  ImageSelector,
  type ImageSelectorProps,
  type RendererProps,
} from "@circle-react-uikit/ImageInput/ImageSelector";
import type { IconName } from "@circleco/compass/components/Icon";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { CompassLikeImageInputRenderer } from "./CompassLikeImageInputRenderer";

export interface CompassLikeImageInputProps extends Omit<
  ImageSelectorProps,
  "renderer" | "value"
> {
  emptyStateDescription: ReactNode;
  emptyStateIconName?: IconName;
  emptyStateTitle?: ReactNode;
  isProcessing?: boolean;
  title?: string;
  value?: ImageSelectorProps["value"];
}

export const CompassLikeImageInput = ({
  alt,
  className,
  emptyStateDescription,
  emptyStateIconName,
  emptyStateTitle,
  isProcessing,
  onChange,
  title,
  value,
  ...rest
}: CompassLikeImageInputProps) => {
  const [processingLabel, setProcessingLabel] = useState<string>();

  useEffect(() => {
    if (!isProcessing && processingLabel) {
      setProcessingLabel(undefined);
    }
  }, [isProcessing, processingLabel]);

  const renderImageInput = useCallback(
    (rendererProps: RendererProps) => {
      const handleChangeImageClick: RendererProps["handleChangeCoverClick"] =
        event => {
          setProcessingLabel(undefined);
          rendererProps.handleChangeCoverClick?.(event);
        };
      const handleDelete: RendererProps["handleDelete"] = event => {
        setProcessingLabel(t("removing"));
        rendererProps.handleDelete(event);
      };

      return (
        <CompassLikeImageInputRenderer
          {...rendererProps}
          emptyStateDescription={emptyStateDescription}
          emptyStateIconName={emptyStateIconName}
          emptyStateTitle={emptyStateTitle}
          handleChangeCoverClick={handleChangeImageClick}
          handleDelete={handleDelete}
          previewAlt={alt}
          processingLabel={processingLabel}
          processing={Boolean(isProcessing || rendererProps.processing)}
        />
      );
    },
    [
      alt,
      emptyStateDescription,
      emptyStateIconName,
      emptyStateTitle,
      isProcessing,
      processingLabel,
    ],
  );

  const imageInputProps = {
    ...rest,
    alt,
    className: classNames("relative min-w-0 w-full", className),
    renderer: renderImageInput,
    title: title ?? t("choose_image"),
  };

  if (value !== undefined || onChange) {
    return (
      <ImageSelector
        {...imageInputProps}
        onChange={onChange}
        value={value ?? ""}
      />
    );
  }

  return <ImageInput {...imageInputProps} />;
};

CompassLikeImageInput.displayName = "CompassLikeImageInput";
